import { Search } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import type { ReactNode } from 'react'

import { Badge, Button, controlClass, mutedTextClass } from '@/components/layout/ui'
import type { BadgeTone } from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { titleCase } from '@/lib/format'
import type { AdminWorkspace } from './types'

/**
 * Small pieces the three console screens share.
 *
 * They live here rather than in `components/layout/ui` because none of them is
 * a general primitive — a plan badge that knows what "comped" means belongs to
 * the console, and putting it in the shared kit would invite the rest of the
 * app to grow opinions about billing sources.
 */

/* ---------------------------------------------------------------------------
 * Tables
 *
 * One rule, applied everywhere: a wide table scrolls inside its OWN container,
 * never by pushing the page sideways. The console's tables have eight or nine
 * columns and are read on laptops.
 * ------------------------------------------------------------------------ */

export function TableScroll({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[52rem] border-collapse text-sm">{children}</table>
    </div>
  )
}

export function Th({
  children,
  numeric,
  className,
}: {
  children: ReactNode
  numeric?: boolean
  className?: string
}) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b border-ink-200 px-4 py-2.5 text-xs font-medium tracking-[0.08em] text-ink-500 uppercase',
        numeric ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  )
}

export function Td({
  children,
  numeric,
  className,
}: {
  children: ReactNode
  numeric?: boolean
  className?: string
}) {
  return (
    <td
      className={cn(
        'border-b border-ink-100 px-4 py-3 align-middle',
        // tabular-nums on every numeric column, so counts line up down the page
        // instead of wandering with the digit widths.
        numeric && 'text-right tabular-nums',
        className,
      )}
    >
      {children}
    </td>
  )
}

/* ---------------------------------------------------------------------------
 * Search
 * ------------------------------------------------------------------------ */

/**
 * A debounced search box.
 *
 * The delay is what keeps a nine-character query from firing nine cross-tenant
 * scans. It holds its own text so typing stays responsive while the committed
 * value — the one that keys the query — lags behind.
 */
export function SearchInput({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
  label: string
}) {
  const id = useId()
  const [text, setText] = useState(value)

  // Re-sync when the committed value changes from outside (a cleared filter).
  useEffect(() => setText(value), [value])

  useEffect(() => {
    if (text === value) return
    const timer = window.setTimeout(() => onChange(text), 250)
    return () => window.clearTimeout(timer)
  }, [text, value, onChange])

  return (
    <div className="relative max-w-sm flex-1">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Search
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
        aria-hidden="true"
      />
      <input
        id={id}
        type="search"
        value={text}
        placeholder={placeholder}
        onChange={(event) => setText(event.target.value)}
        className={cn(controlClass, 'pl-9')}
      />
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * Plan and status badges
 * ------------------------------------------------------------------------ */

/** Where a workspace's plan came from. `manual` is the one this console makes. */
const SOURCE_TONE: Record<string, BadgeTone> = {
  stripe: 'success',
  apple: 'brand',
  trial: 'warning',
  manual: 'warning',
  none: 'neutral',
}

const SOURCE_LABEL: Record<string, string> = {
  stripe: 'Stripe',
  apple: 'App Store',
  trial: 'Trial',
  manual: 'Comped',
  none: 'Free',
}

/**
 * The plan, with the SOURCE beside it.
 *
 * The source is the whole point of the column. "Business" alone does not tell
 * you whether a workspace is paying, on a comp that expires on Friday, or
 * grandfathered — and those are three different conversations.
 */
export function PlanBadge({ workspace }: { workspace: AdminWorkspace }) {
  const source = workspace.plan_source || 'none'
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="font-medium">{titleCase(workspace.plan)}</span>
      <Badge tone={SOURCE_TONE[source] ?? 'neutral'}>{SOURCE_LABEL[source] ?? titleCase(source)}</Badge>
    </span>
  )
}

const SUB_TONE: Record<string, BadgeTone> = {
  active: 'success',
  trialing: 'brand',
  past_due: 'warning',
  unpaid: 'danger',
  canceled: 'neutral',
  incomplete: 'warning',
  incomplete_expired: 'neutral',
}

export function SubscriptionBadge({ status }: { status: string }) {
  if (!status) return <span className={mutedTextClass}>—</span>
  return <Badge tone={SUB_TONE[status] ?? 'neutral'}>{titleCase(status)}</Badge>
}

/** `3 / 10`, or `3 / ∞` when the tier has no cap. */
export function Seats({ used, limit }: { used: number; limit: number }) {
  const unlimited = limit <= 0
  const full = !unlimited && used >= limit
  return (
    <span className={cn('tabular-nums', full && 'font-medium text-amber-700')}>
      {used} / {unlimited ? '∞' : limit}
    </span>
  )
}

/* ---------------------------------------------------------------------------
 * Pagination
 * ------------------------------------------------------------------------ */

export function Pager({
  page,
  perPage,
  total,
  onPage,
  noun,
}: {
  page: number
  perPage: number
  total: number
  onPage: (next: number) => void
  noun: string
}) {
  const lastPage = Math.max(1, Math.ceil(total / Math.max(perPage, 1)))
  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to = Math.min(page * perPage, total)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <p className={cn('text-xs tabular-nums', mutedTextClass)}>
        {total === 0 ? `No ${noun}` : `${from}–${to} of ${total} ${noun}`}
      </p>
      {lastPage > 1 ? (
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            Previous
          </Button>
          <span className={cn('text-xs tabular-nums', mutedTextClass)}>
            Page {page} of {lastPage}
          </span>
          <Button size="sm" disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
            Next
          </Button>
        </div>
      ) : null}
    </div>
  )
}
